import {
  ChangeEvent,
  ComponentType,
  FocusEvent,
  forwardRef,
  KeyboardEvent,
  memo,
  ReactNode,
  Ref,
  SyntheticEvent,
  useCallback,
  useRef,
  useState
} from 'react'
import cn from 'classnames'
import { TGroupPosition } from 'components/types/ui'
import { getTypedCn } from 'helpers/getTypedCn'

import s from './InputBase.module.less'

export type TElementPosition =
  | 'left'
  | 'middle'
  | 'right'
  | 'top'
  | 'bottom'
  | 'topLeft'
  | 'topRight'
  | 'topMiddle'
  | 'middleLeft'
  | 'middleMiddle'
  | 'middleRight'
  | 'bottomLeft'
  | 'bottomMiddle'
  | 'bottomRight'

export type TInputSize = 'sm' | 'nm' | 'lg'
export type TInputValue = string | number | boolean

export interface IInputBaseCommonProps {
  /** Флаг автокомлита */
  autoComplete?: string
  /** Флаг установки фокуса */
  autoFocus?: boolean
  /** Кастомное название класса для обертки над инпутом */
  className?: string
  /** Значение по умолчанию */
  defaultValue?: TInputValue
  /** Флаг: заблокировано поле или нет */
  disabled?: boolean
  /** Текст ошибки или флаг её наличия */
  error?: boolean | string
  errorLabel?: boolean | string
  /** Позиция инпута, необходима для закругления определенных границ в зависимости от позиции */
  groupPositionH?: TGroupPosition
  groupPositionV?: TGroupPosition
  id?: string
  name?: string
  placeholder?: string
  /** Флаг: только для чтения */
  readOnly?: boolean
  /** Размер */
  size?: TInputSize
  /** Идентификатор для тестов React Testing Library */
  testId?: string
  /** Типы ипута (checkbox, text, password и т.д) */
  type?: string
  /** Введенный текст */
  value?: TInputValue
  /** Текст предупреждения или флаг его наличия */
  warning?: boolean | string
}

export interface IInputBaseEventProps {
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void
  onChange?: (value: string, event?: ChangeEvent<HTMLInputElement>) => void
  onClick?: (event: SyntheticEvent<HTMLDivElement>) => void
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
  onKeyUp?: (event: KeyboardEvent<HTMLInputElement>) => void
}

export interface IInputComponentGeneralProps
  extends Omit<IInputBaseCommonProps, 'size' | 'error' | 'warning'>,
    Omit<IInputBaseEventProps, 'onClick' | 'onChange'> {
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
}

export interface IInputBaseProps<InputProps extends {}> extends IInputBaseCommonProps, IInputBaseEventProps {
  children?: ReactNode
  /* Кастомные компонент на место инпута (по умолчанию: <input />) */
  inputComponent?: ComponentType<IInputComponentGeneralProps & InputProps>
  /* Пропсы для инпута */
  inputProps?: InputProps
  /* Ref для инпута */
  inputRef?: Ref<HTMLInputElement>
  postfix?: ReactNode
  prefix?: ReactNode
}

export interface IInputPropsGenericExtend {
  [key: string]: any
  className?: string
  ref?: Ref<HTMLInputElement>
}

export const InputBase = <InputProps extends IInputPropsGenericExtend>({
  autoComplete,
  autoFocus,
  value,
  id,
  name,
  type = 'text',
  groupPositionH,
  groupPositionV,
  size = 'nm',
  readOnly,
  disabled,
  defaultValue,
  error,
  errorLabel,
  warning,
  className,
  placeholder,
  onClick,
  onChange,
  onKeyDown,
  onKeyUp,
  onFocus,
  onBlur,
  prefix,
  postfix,
  children,
  inputRef: inputRefProp,
  inputComponent,
  inputProps = {} as InputProps,
  testId
}: IInputBaseProps<InputProps>) => {
  const [focused, setFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const { ref: refFromInputProps, className: cnFromInputProps, ...restInputProps } = inputProps

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (disabled) {
        event.stopPropagation()

        return
      }

      setFocused(true)

      if (onFocus) {
        onFocus(event)
      }
    },
    [disabled, onFocus]
  )

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      setFocused(false)

      if (onBlur) {
        onBlur(event)
      }
    },
    [onBlur]
  )

  const handleClick = useCallback(
    (event: SyntheticEvent<HTMLDivElement>) => {
      if (event.currentTarget === event.target) {
        inputRef?.current?.focus()
      }

      if (onClick) {
        onClick(event)
      }
    },
    [onClick]
  )

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(event.target.value, event)
      }
    },
    [onChange]
  )

  const handleRefInput = useCallback(
    (ref: HTMLInputElement | null) => {
      inputRef.current = ref

      let refProp: Ref<HTMLInputElement> = null

      if (inputRefProp) {
        refProp = inputRefProp
      } else if (refFromInputProps) {
        refProp = refFromInputProps
      }

      if (typeof refProp === 'function') {
        refProp(ref)
      } else if (refProp) {
        // TODO исправить
        // @ts-ignore
        refProp.current = ref
      }
    },
    [refFromInputProps, inputRefProp]
  )

  const inputWrapperClassName = cn(
    s.InputBase,
    className,
    getTypedCn(s, 'InputBase_size_', size),
    getTypedCn(s, 'InputBase_group-position-h_', groupPositionH),
    getTypedCn(s, 'InputBase_group-position-v_', groupPositionV),
    {
      [s.InputBase_focused]: focused,
      [s.InputBase_error]: error,
      [s.InputBase_warning]: !error && warning,
      [s.InputBase_focused_error]: error && focused,
      [s.InputBase_focused_warning]: !error && warning && focused,
      [s.InputBase_readOnly]: readOnly,
      [s.InputBase_disabled]: disabled,
      [s.InputBase_prefix]: prefix,
      [s.InputBase_postfix]: postfix
    }
  )

  const inputComponentProps = {
    id,
    className: cn(s.input, cnFromInputProps),
    name,
    placeholder,
    type,
    autoComplete,
    autoFocus,
    value,
    defaultValue,
    readOnly,
    disabled,
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onKeyDown,
    onKeyUp,
    ref: handleRefInput,
    ...restInputProps
  } as IInputComponentGeneralProps & InputProps

  const InputComponent = inputComponent || 'input'

  return (
    <>
      <div
        className={inputWrapperClassName}
        data-error={error}
        data-name={name}
        data-warning={warning}
        onClick={handleClick}
      >
        {prefix ? <div className={s.prefix}>{prefix}</div> : null}
        {/* @ts-expect-error */}
        <InputComponent {...inputComponentProps} data-test-id={testId} />

        {postfix ? <div className={s.postfix}>{postfix}</div> : null}

        {children ? <div>{children}</div> : null}
      </div>
      {errorLabel ? <div className={s.errorLabel}>{errorLabel}</div> : ''}
    </>
  )
}

export const InputBaseMemo = memo(
  forwardRef((props: IInputBaseProps<any>, ref: Ref<HTMLInputElement>) => <InputBase {...props} inputRef={ref} />)
) as <InputProps extends IInputPropsGenericExtend>(
  props: Omit<IInputBaseProps<InputProps>, 'inputRef'> & {
    ref?: Ref<HTMLInputElement>
  }
) => JSX.Element
